import { AlertTitle, type SxProps, type Theme } from '@mui/material'
import base64 from 'base-64'
import Big, { type BigSource } from 'big.js'
import React, { Fragment } from 'react'
import { FormattedErrorMessage } from '@/helpers/FormattedErrorMessage'
import { T } from '@/helpers/T'
import type { EClass, TGrailsValidationError } from '@/modules/_misc'
import type { EDomainClass } from '@/modules/_misc/auto/enums/DomainClass'
import { DomainTree } from '@/modules/_misc/auto/enums/DomainTree'
import { EFuelQuantityUnit } from '@/modules/_misc/enums/FuelQuantityUnit'
import type {
  IDigitalFileWContent,
  IDigitalFileWContentParsed,
} from '@/modules/DigitalFile/DigitalFile'
import type { TEndpoint, TEndpointObjectEnsured } from './types'

//	///////////////////////
//	MARK: CONFIG
export const CONFIG = {
  timeZones: {
    accounting: 'Europe/Brussels',
    humanResources: 'UTC',
    operations: 'UTC',
  },
}

//	///////////////////////
//	MARK: isDomainClass
export const isDomainClass = (domainClass: EDomainClass | EClass, value: EDomainClass | EClass) => {
  const classNames = DomainTree[domainClass as EDomainClass].classNames as unknown as EDomainClass[]

  return classNames.includes(value as EDomainClass)
}

//	///////////////////////
//	MARK: isReactComponent
export const REACT_PORTAL_TYPE = Symbol.for('react.portal')
export const REACT_FRAGMENT_TYPE = Symbol.for('react.fragment')
export const REACT_STRICT_MODE_TYPE = Symbol.for('react.strict_mode')
export const REACT_PROFILER_TYPE = Symbol.for('react.profiler')
export const REACT_PROVIDER_TYPE = Symbol.for('react.provider')
export const REACT_CONTEXT_TYPE = Symbol.for('react.context')
export const REACT_SERVER_CONTEXT_TYPE = Symbol.for('react.server_context')
export const REACT_FORWARD_REF_TYPE = Symbol.for('react.forward_ref')
export const REACT_SUSPENSE_TYPE = Symbol.for('react.suspense')
export const REACT_SUSPENSE_LIST_TYPE = Symbol.for('react.suspense_list')
export const REACT_MEMO_TYPE = Symbol.for('react.memo')
export const REACT_LAZY_TYPE = Symbol.for('react.lazy')
export const REACT_SCOPE_TYPE = Symbol.for('react.scope')
export const REACT_DEBUG_TRACING_MODE_TYPE = Symbol.for('react.debug_trace_mode')
export const REACT_OFFSCREEN_TYPE = Symbol.for('react.offscreen')
export const REACT_LEGACY_HIDDEN_TYPE = Symbol.for('react.legacy_hidden')
export const REACT_CACHE_TYPE = Symbol.for('react.cache')
export const REACT_TRACING_MARKER_TYPE = Symbol.for('react.tracing_marker')
export const REACT_SERVER_CONTEXT_DEFAULT_VALUE_NOT_LOADED = Symbol.for('react.default_value')

export function isReactComponent<T>(object: unknown): object is React.FC<T> {
  if (typeof object === 'function') {
    return true
  }

  if (
    object &&
    typeof object === 'object' &&
    '$$typeof' in object &&
    typeof object.$$typeof === 'symbol'
  ) {
    const types = [
      REACT_PORTAL_TYPE,
      REACT_FRAGMENT_TYPE,
      REACT_STRICT_MODE_TYPE,
      REACT_PROFILER_TYPE,
      REACT_PROVIDER_TYPE,
      REACT_CONTEXT_TYPE,
      REACT_SERVER_CONTEXT_TYPE,
      REACT_FORWARD_REF_TYPE,
      REACT_SUSPENSE_TYPE,
      REACT_SUSPENSE_LIST_TYPE,
      REACT_MEMO_TYPE,
      REACT_LAZY_TYPE,
      REACT_SCOPE_TYPE,
      REACT_DEBUG_TRACING_MODE_TYPE,
      REACT_OFFSCREEN_TYPE,
      REACT_LEGACY_HIDDEN_TYPE,
      REACT_CACHE_TYPE,
      REACT_TRACING_MARKER_TYPE,
      REACT_SERVER_CONTEXT_DEFAULT_VALUE_NOT_LOADED,
    ].map((e) => e.toString())

    if (types.includes(object.$$typeof.toString())) {
      return true
    }
  }

  return false
}

//	///////////////////////
//	MARK: renderComponentOrNode
export type TRenderComponentOrNodeValue<T = {}, NotNul extends boolean = false> =
  | React.FC<T>
  | (NotNul extends true ? NonNullable<React.ReactNode> : React.ReactNode)
  | undefined
  | (
      | React.FC<T>
      | (NotNul extends true ? NonNullable<React.ReactNode> : React.ReactNode)
      | undefined
    )[]

export function renderComponentOrNode<T, NotNul extends boolean = false>(
  value: TRenderComponentOrNodeValue<T, NotNul>,
  props: T & { children?: React.ReactNode },
  defaultValue?: React.ReactNode,
): React.ReactNode {
  if (Array.isArray(value)) {
    return value.map((e, i) => (
      <Fragment key={i}>{renderComponentOrNode(e, props, defaultValue)}</Fragment>
    ))
  }

  if (isReactComponent<T>(value)) {
    const Component = value
    return <Component {...props} />
  }

  if (React.isValidElement(value)) {
    return value ?? defaultValue
  }

  return defaultValue
}

//	///////////////////////
//	MARK: parseParams
export const parseParams = (
  params: object,
  initialSearchParams?: URLSearchParams,
): URLSearchParams => {
  const urlSearchParams = initialSearchParams ?? new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    let v: string | number | boolean | undefined

    if (value instanceof Date) {
      urlSearchParams.set(key, value.toISOString())
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      v = value
    } else if (Array.isArray(value)) {
      v = value.join(',')
    }

    if (v !== undefined) {
      urlSearchParams.set(key, v.toString())
    } else if (value && typeof value === 'object' && 'id' in value) {
      urlSearchParams.set(`${key}Id`, encodeURIComponent(value.id as number))
    }
  }

  return urlSearchParams
}

//	///////////////////////
//	MARK: parseEndpointObject
export const parseEndpointObject = (
  endpoint: string,
  params?: object,
  match?: Record<string, string | number>,
): string => {
  const urlSearchParams = parseParams(params ?? {})

  if (match) {
    for (const [key, value] of Object.entries(match)) {
      if (value !== undefined) {
        endpoint = endpoint.replace(`:${key}`, value.toString())
      }
    }
  }

  if ([...urlSearchParams.keys()].length > 0) {
    return `${endpoint}?${urlSearchParams.toString()}`
  }

  return endpoint
}

//	///////////////////////
//	MARK: parseEndpoint
export const parseEndpoint = (endpoint: TEndpoint): string =>
  typeof endpoint === 'string'
    ? parseEndpointObject(endpoint)
    : parseEndpointObject(endpoint.url, endpoint.params, endpoint.match)

//	///////////////////////
//	MARK: ensureEndpointObject
export const ensureEndpointObject = (endpoint: TEndpoint): TEndpointObjectEnsured =>
  typeof endpoint === 'string'
    ? {
        url: endpoint,
        match: {},
        params: {},
      }
    : {
        url: endpoint.url,
        match: endpoint.match ?? {},
        params: endpoint.params ?? {},
      }

//	///////////////////////
//	MARK: toRad
export const toRad = (v: number) => (v * Math.PI) / 180

//	///////////////////////
//	MARK: calcCrow
export const calcCrow = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6372.8
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  lat1 = toRad(lat1)
  lat2 = toRad(lat2)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

//	///////////////////////
//	MARK: joinSXProps
export const joinSXProps = (...props: (SxProps<Theme> | undefined | null)[]): SxProps<Theme> => {
  const sx: SxProps<Theme>[] = []

  for (const prop of props) {
    if (prop) {
      sx.push(prop)
    }
  }

  return [...sx] as SxProps<Theme>
}

//	///////////////////////
//	MARK: isFunction
export const isFunction = (functionToCheck: unknown): functionToCheck is Function =>
  !!functionToCheck && Object.prototype.toString.call(functionToCheck) === '[object Function]'

//	///////////////////////
//	MARK: isPromise
export const isPromise = <T extends Function>(object: unknown): object is Promise<T> => {
  if (Promise?.resolve) {
    return Promise.resolve(object) === object
  }

  return false
}

//	///////////////////////
//	MARK: isGrailsValidationError
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isGrailsValidationError = (object: any): object is TGrailsValidationError =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, no-underscore-dangle
  typeof object === 'object' && object._embedded && Array.isArray(object._embedded.errors)

//	///////////////////////
//	MARK: parseGrailsValidationError
// Un refus de règle métier (FrontEndException) arrive avec une clé i18n (e.mtx.*, e.sfy.*…) : on la traduit,
// sans le titre « entité non traitable » réservé aux erreurs de validation Grails.
const I18N_KEY_PATTERN = /^[a-z][a-z0-9]*(\.[A-Za-z0-9_]+)+$/

export const parseGrailsValidationError = (object: TGrailsValidationError): React.ReactNode => {
  // eslint-disable-next-line no-underscore-dangle
  const errors = object._embedded.errors
  const ruleKeys = errors.every((error) => I18N_KEY_PATTERN.test(error.message ?? ''))

  return (
    <>
      {ruleKeys ? null : (
        <AlertTitle>
          <T id='e.unprocessable_entity' />
        </AlertTitle>
      )}
      {errors.map((error, i) => (
        <Fragment key={i}>
          {i > 0 && <br />}
          {ruleKeys ? <FormattedErrorMessage id={error.message} /> : error.message}
        </Fragment>
      ))}
    </>
  )
}

//	///////////////////////
//	MARK: isGoodMimeType
type TMime = {
  mime: string
  pattern: number[]
  mask: number[]
}

export const MIMES: TMime[] = [
  {
    mime: 'image/jpeg',
    pattern: [0xff, 0xd8, 0xff],
    mask: [0xff, 0xff, 0xff],
  },
  {
    mime: 'image/png',
    pattern: [0x89, 0x50, 0x4e, 0x47],
    mask: [0xff, 0xff, 0xff, 0xff],
  },
  {
    mime: 'application/pdf',
    pattern: [0x25, 0x50, 0x44, 0x46, 0x2d],
    mask: [0xff, 0xff, 0xff, 0xff],
  },
]

export function checkBytesForMime(bytes: Uint8Array, mime: TMime) {
  for (let i = 0, l = mime.mask.length; i < l; ++i) {
    // eslint-disable-next-line no-bitwise
    if ((bytes[i] & mime.mask[i]) - mime.pattern[i] !== 0) {
      return false
    }
  }
  return true
}

export function checkBytes(bytes: Uint8Array): string | null {
  for (let i = 0, l = MIMES.length; i < l; ++i) {
    if (checkBytesForMime(bytes, MIMES[i])) {
      return MIMES[i].mime
    }
  }

  return null
}

export const isGoodMimeType = async (file: File): Promise<string | null> =>
  new Promise((resolve) => {
    const reader = new FileReader()

    reader.onloadend = (e) => {
      if (e.target?.readyState === FileReader.DONE && e.target.result instanceof ArrayBuffer) {
        return resolve(checkBytes(new Uint8Array(e.target.result)))
      }
    }

    // eslint-disable-next-line unicorn/prefer-blob-reading-methods
    reader.readAsArrayBuffer(file.slice(0, 4))
  })

//	///////////////////////
//	MARK: convertDigitalFileToBase64
export function convertDigitalFileToBase64(
  value: IDigitalFileWContent,
): IDigitalFileWContentParsed {
  const bytes = new Uint8Array(value.fileContent)

  const mimeType = checkBytes(bytes) ?? value.mimeType

  return {
    clazz: value.clazz,
    id: value.id,
    fileName: value.fileName,
    mimeType,

    base64: `data:${mimeType};base64,${base64.encode(
      bytes.reduce((data, byte) => data + String.fromCodePoint(byte), ''),
    )}`,
  }
}

//  ///////////////////////
//  MARK: convertFuelQuantity
export const convertFuelQuantity = (
  value: BigSource,
  unit: EFuelQuantityUnit,
  fromLtr = false,
): number => {
  const pow = fromLtr ? -1 : 1

  const newValue = Big(value)

  switch (unit) {
    case EFuelQuantityUnit.USG: {
      return newValue.times(Big(3.785_41).pow(pow)).round(2).toNumber()
    }
    case EFuelQuantityUnit.LBS: {
      return newValue.times(Big(1.76).pow(pow)).round(2).toNumber()
    }
    case EFuelQuantityUnit.KGS: {
      return newValue.times(Big(0.8).pow(pow)).round(3).toNumber()
    }
    default: {
      return newValue.round(2).toNumber()
    }
  }
}

//  ///////////////////////
//  MARK: getCoordinates
async function getCoordinates(address: string): Promise<{ lat: number; lon: number }> {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`,
  )

  const data = (await response.json()) as {
    lat: string
    lon: string
  }[]

  if (data && data.length > 0) {
    return {
      lat: Number.parseFloat(data[0].lat),
      lon: Number.parseFloat(data[0].lon),
    }
  }

  throw new Error('No coordinates found for this address')
}
